/**
 * edgeWaypoints — pure geometry for user-routed transition edges (ENG-1061).
 *
 * A State Machine transition can carry `data.waypoints`: an ordered list of
 * `{x, y}` points (in ReactFlow flow coordinates) the wire is routed THROUGH,
 * Unreal-reroute-node style. Double-clicking a transition drops one; dragging
 * it reshapes the curve; double-clicking it removes it. These helpers build the
 * SVG path and decide where a new waypoint is inserted — kept pure and separate
 * from the React edge component so they can be unit-tested without a canvas.
 *
 * All points are `{x, y}`; every path helper returns an SVG path `d` string
 * whose first command is `M` (so an empty/short input yields '').
 */

/** Straight polyline through the points: `M x0,y0 L x1,y1 …`. */
export function polylinePath(points) {
  if (!points || points.length < 2) return ''
  let d = `M ${points[0].x},${points[0].y}`
  for (let i = 1; i < points.length; i++) d += ` L ${points[i].x},${points[i].y}`
  return d
}

/**
 * Smooth Catmull-Rom spline through the points, emitted as cubic beziers
 * (`M … C …`). The curve passes through every point; endpoints are duplicated
 * so the first/last segments don't overshoot. Guarantees a `C` command for any
 * input with ≥2 points (so callers can treat it as the "arched" shape).
 *
 * `tension` is the control-point offset factor (`(p2 - p0) * tension`): larger
 * = a more pronounced bend, 0 = the straight polyline. Defaults to `1/6`, the
 * historical value, so existing callers are unchanged; the SM editor drives it
 * from the arched-spline curvature preference (splineTensionForCurvature).
 */
export function smoothSplinePath(points, tension = 1 / 6) {
  if (!points || points.length < 2) return ''
  let d = `M ${points[0].x},${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] || p2
    const c1x = p1.x + (p2.x - p0.x) * tension
    const c1y = p1.y + (p2.y - p0.y) * tension
    const c2x = p2.x - (p3.x - p1.x) * tension
    const c2y = p2.y - (p3.y - p1.y) * tension
    d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`
  }
  return d
}

/**
 * Curvature-scaled control-point offset for `bowedBezierPath`.
 *
 * ReactFlow's own getBezierPath ignores `curvature` whenever the control
 * distance is >= 0 (it hardcodes `0.5 * distance`), so on a left-to-right state
 * machine — where most transitions run "forward" — the curvature knob does
 * nothing. We restore its effect by scaling that branch with curvature too:
 * `2 * curvature * distance` equals `0.5 * distance` exactly at the default
 * curvature (0.25 → 0.5), so a default edge is bit-identical to ReactFlow's,
 * while the slider now visibly tightens/loosens EVERY edge (curvature 0 →
 * control points collapse onto the endpoints → a near-straight line). The
 * negative branch is ReactFlow's own, which already scales with curvature.
 */
function bezierControlOffset(distance, curvature) {
  if (distance >= 0) return 2 * curvature * distance
  return curvature * 25 * Math.sqrt(-distance)
}

// Control point for one endpoint, offset along its handle direction. `pos` is a
// ReactFlow Position string ('left' | 'right' | 'top' | 'bottom').
function bezierControlPoint(pos, x1, y1, x2, y2, c) {
  switch (pos) {
    case 'left':  return [x1 - bezierControlOffset(x1 - x2, c), y1]
    case 'right': return [x1 + bezierControlOffset(x2 - x1, c), y1]
    case 'top':   return [x1, y1 - bezierControlOffset(y1 - y2, c)]
    default:      return [x1, y1 + bezierControlOffset(y2 - y1, c)]  // 'bottom'
  }
}

/**
 * Cubic bezier between two handles whose bend scales with `curvature`, drop-in
 * for ReactFlow's getBezierPath (same path-string format, same handle-tangent
 * feel) but with a curvature knob that works on forward edges. Returns
 * `[path, labelX, labelY]`; the label sits at the curve's t=0.5 midpoint.
 */
export function bowedBezierPath({
  sourceX, sourceY, sourcePosition = 'bottom',
  targetX, targetY, targetPosition = 'top',
  curvature = 0.25,
}) {
  const [scx, scy] = bezierControlPoint(sourcePosition, sourceX, sourceY, targetX, targetY, curvature)
  const [tcx, tcy] = bezierControlPoint(targetPosition, targetX, targetY, sourceX, sourceY, curvature)
  // Cubic evaluated at t=0.5: 1/8·P0 + 3/8·C1 + 3/8·C2 + 1/8·P3.
  const labelX = 0.125 * sourceX + 0.375 * scx + 0.375 * tcx + 0.125 * targetX
  const labelY = 0.125 * sourceY + 0.375 * scy + 0.375 * tcy + 0.125 * targetY
  const path = `M${sourceX},${sourceY} C${scx},${scy} ${tcx},${tcy} ${targetX},${targetY}`
  return [path, labelX, labelY]
}

/** Shortest distance from point `p` to segment `a`–`b`. */
export function distToSegment(p, a, b) {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  let t = len2 === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return Math.hypot(p.x - cx, p.y - cy)
}

/**
 * Index into a waypoint array at which to insert a new point clicked at `p`,
 * given the FULL routed point list `pts = [source, ...waypoints, target]`.
 * Finds the segment nearest the click; a new waypoint inserted there keeps the
 * route in visual order. Returns a value in [0, waypoints.length].
 */
export function insertIndexForClick(pts, p) {
  if (!pts || pts.length < 2) return 0
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < pts.length - 1; i++) {
    const d = distToSegment(p, pts[i], pts[i + 1])
    if (d < bestD) { bestD = d; best = i }
  }
  return best // segment i sits between pts[i] and pts[i+1] → waypoint index i
}
